import React, { useState } from 'react';
import { motion } from 'framer-motion';
import SymmetryAxisGame from './SymmetryAxisGame';
import SymmetryGridGame from './SymmetryGridGame';
import SymmetryLineGame from './SymmetryLineGame';
import { sounds } from '../../utils/SoundManager';

const TabButton = ({ isActive, onClick, children }) => (
    <button
        onClick={() => {
            sounds.playAction();
            if (onClick) onClick();
        }}
        className={`
            px-6 py-3 rounded-full font-bold text-lg transition-all
            ${isActive
                ? 'bg-brand-purple text-white shadow-lg scale-105'
                : 'bg-white text-gray-500 hover:bg-purple-50 hover:text-brand-purple'}
        `}
    >
        {children}
    </button>
);

const Section = ({ title, children, className }) => (
    <section className={`bg-white rounded-3xl p-6 md:p-10 shadow-xl border-4 border-brand-blue/10 ${className}`}>
        <h2 className="text-2xl md:text-3xl font-bold text-brand-purple mb-8 font-hand text-center">{title}</h2>
        {children}
    </section>
);

const Symmetry = () => {
    const [activeTab, setActiveTab] = useState('axis'); // 'axis' | 'grid'
    const [gridMode, setGridMode] = useState('pixel'); // 'pixel' | 'line'

    return (
        <div className={`mx-auto pb-20 space-y-8 transition-all duration-500 ${activeTab === 'grid' ? 'max-w-[95vw] xl:max-w-7xl' : 'max-w-4xl'}`}>
            <header className="text-center space-y-2 mb-12">
                <span className="bg-brand-purple text-white px-4 py-1 rounded-full font-bold text-sm tracking-wide uppercase">
                    Unidad 1
                </span>
                <h1 className="text-5xl font-extrabold text-gray-800 tracking-tight">¡Simetría! 🦋</h1>
                <p className="text-xl text-gray-500 font-medium">Cuando dos partes son iguales, como en un espejo.</p>
            </header>

            {/* Navigation Tabs */}
            <div className="flex justify-center gap-4 mb-8">
                <TabButton isActive={activeTab === 'axis'} onClick={() => setActiveTab('axis')}>
                    1. Traza el Eje ✏️
                </TabButton>
                <TabButton isActive={activeTab === 'grid'} onClick={() => setActiveTab('grid')}>
                    2. Completa el Dibujo 🎨
                </TabButton>
            </div>

            <motion.div
                key={activeTab}
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                transition={{ duration: 0.3 }}
            >
                {activeTab === 'axis' && (
                    <Section title="¿Dónde está el eje?">
                        <div className="text-center mb-8 bg-blue-50 p-6 rounded-2xl">
                            <p className="text-lg text-gray-700">
                                Una figura es simétrica cuando al doblarla las dos partes coinciden.
                                <br />
                                <strong>Haz clic en el centro</strong> de estas figuras para dibujar su eje de simetría.
                            </p>
                        </div>
                        <SymmetryAxisGame />
                    </Section>
                )}

                {activeTab === 'grid' && (
                    <Section title="Espejo Mágico">
                        <div className="flex justify-center gap-4 mb-6">
                            <button
                                onClick={() => {
                                    sounds.playAction();
                                    setGridMode('pixel');
                                }}
                                className={`px-4 py-2 rounded-xl font-bold transition-colors ${gridMode === 'pixel' ? 'bg-orange-100 text-brand-orange border-2 border-brand-orange' : 'bg-gray-50 text-gray-400 hover:bg-gray-100'}`}
                            >
                                🧱 Bloques
                            </button>
                            <button
                                onClick={() => {
                                    sounds.playAction();
                                    setGridMode('line');
                                }}
                                className={`px-4 py-2 rounded-xl font-bold transition-colors ${gridMode === 'line' ? 'bg-blue-100 text-brand-blue border-2 border-brand-blue' : 'bg-gray-50 text-gray-400 hover:bg-gray-100'}`}
                            >
                                📏 Líneas
                            </button>
                        </div>

                        <div className="text-center mb-8 bg-orange-50 p-6 rounded-2xl">
                            <p className="text-lg text-gray-700">
                                ¡Completa el dibujo para que sea igual a los dos lados de la línea roja!
                            </p>
                        </div>

                        {gridMode === 'pixel' ? <SymmetryGridGame /> : <SymmetryLineGame />}
                    </Section>
                )}
            </motion.div>
        </div>
    );
};

export default Symmetry;
